import { useState, useEffect, useRef, useCallback } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';

import '../../styles/ProfileSettings.css';
import { ReactComponent as DefaultAvatar } from '../../react-icons/profileIcon.svg';
import { showMessage } from '../../showMessage';
const API_BASE_URL_MISSING_KEY = 'profile.apiBaseMissing';
const NOTIF_ERROR = 'notifications.error';
const NOTIF_SUCCESS = 'notifications.success';

type ApiResponse = { success: boolean; message?: string };
type UserSettingsResponse = ApiResponse & {
  user?: { name: string; lastName: string; email: string; phoneNumber: string; avatar?: string };
};
function isApiResponse(value: unknown): value is ApiResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const v = value as Record<string, unknown>;
  return (
    typeof v.success === 'boolean' && (v.message === undefined || typeof v.message === 'string')
  );
}
function isUserSettingsResponse(value: unknown): value is UserSettingsResponse {
  if (!isApiResponse(value)) {
    return false;
  }
  const v = value as Record<string, unknown>;
  const user = v.user as Record<string, unknown> | undefined;
  if (user === undefined) {
    return true;
  }
  return (
    typeof user.name === 'string' &&
    typeof user.lastName === 'string' &&
    typeof user.email === 'string' &&
    typeof user.phoneNumber === 'string' &&
    (user.avatar === undefined || typeof user.avatar === 'string')
  );
}
/**
 * Compresses an image to an approximate size by reducing its dimensions and quality.
 * @param file - The original image file.
 * @param maxSizeMB - Maximum allowed size in MB.
 * @param maxDimension - Maximum dimension in px (will be used as maximum width/height).
 * @returns Promise<File> - Returns a new compressed File.
 */
const compressImage = (file: File, maxSizeMB = 2, maxDimension = 1200): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.addEventListener('load', () => {
      const img = new Image();
      img.src = reader.result as string;

      img.addEventListener('load', () => {
        let width = img.width;
        let height = img.height;

        // Scale the image if it exceeds maximum dimensions
        if (width > height) {
          if (width > maxDimension) {
            height = Math.floor(height * (maxDimension / width));
            width = maxDimension;
          }
        } else {
          if (height > maxDimension) {
            width = Math.floor(width * (maxDimension / height));
            height = maxDimension;
          }
        }

        // Create a canvas to draw the image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext('2d');
        context?.drawImage(img, 0, 0, width, height);

        // Internal function to repeatedly reduce quality until the blob is under maxSizeMB
        const tryCompress = (quality: number) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Could not compress the image'));
                return;
              }
              // If the blob is below the desired size in MB or quality < 0.1, finish.
              if (blob.size <= maxSizeMB * 1024 * 1024 || quality < 0.1) {
                const compressedFile = new File([blob], file.name, { type: file.type });
                resolve(compressedFile);
              } else {
                // Reduce quality further and try again.
                tryCompress(quality - 0.1);
              }
            },
            file.type,
            quality,
          );
        };

        // Start with quality 0.8 (adjust as needed)
        tryCompress(0.8);
      });

      img.addEventListener('error', (event_) => reject(event_ as unknown as Error));
    });
    reader.addEventListener('error', (event_) => reject(event_ as unknown as Error));
  });
};

export const ProfileSettings = () => {
  const { t } = useTranslation('common');
  const [userData, setUserData] = useState({
    name: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    avatar: '', // Can be URL/base64
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Reference for the file input
  const fileInputReference = useRef<HTMLInputElement | null>(null);

  /**
   * Function to fetch the avatar from the DB using POST and axios.
   * It handles the case where the JSON response message is "El usuario no tiene avatar".
   */
  const fetchAvatar = useCallback(async () => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!baseUrl) {
      showMessage('error', t(NOTIF_ERROR), t(API_BASE_URL_MISSING_KEY));
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${baseUrl}/api/user/getAvatarFromDB`,
        {},
        {
          headers: { Authorization: `Bearer ${token ?? ''}` },
          responseType: 'blob',
        },
      );
      const data: unknown = response.data;
      if (response.status === 200 && data instanceof Blob) {
        if (data.type === 'application/json' || data.type.startsWith('application/json')) {
          const text = await data.text();
          let json: unknown;
          try {
            json = JSON.parse(text);
          } catch {
            json = undefined;
          }
          if (
            isApiResponse(json) &&
            json.success &&
            json.message === 'User does not have an avatar'
          ) {
            setUserData((previous) => ({ ...previous, avatar: '' }));
          }
        } else if (data.size > 0) {
          const url = URL.createObjectURL(data);
          setUserData((previous) => ({ ...previous, avatar: url }));
        }
      }
    } catch (error) {
      console.error('Error fetching the avatar:', error);
    }
  }, [t]);

  useEffect(() => {
    const fetchUserData = async () => {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      if (!baseUrl) {
        showMessage('error', t(NOTIF_ERROR), t(API_BASE_URL_MISSING_KEY));
        setIsLoading(false);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const userResponse = await axios.post(
          `${baseUrl}/api/user/getUserSettingsInfo`,
          {},
          { headers: { Authorization: `Bearer ${token ?? ''}` } },
        );

        const userPayload: unknown = userResponse.data;
        if (isUserSettingsResponse(userPayload) && userPayload.success) {
          const user = userPayload.user;
          if (user) {
            setUserData({
              name: user.name,
              lastName: user.lastName,
              email: user.email,
              phoneNumber: user.phoneNumber,
              avatar: user.avatar ?? '',
            });
          }
          await fetchAvatar();
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchUserData();
  }, [fetchAvatar, t]);

  // Update user fields
  const handleInputChange = (event_: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event_.target;
    setUserData((previous) => ({ ...previous, [name]: value }));
  };

  // Opens the dialog to change the avatar
  const handleChangeAvatarClick = () => {
    if (fileInputReference.current) {
      fileInputReference.current.click();
    }
  };

  // Reads the selected file, compresses it if it's larger than 2MB, updates the state and uploads the avatar to the server.
  const handleAvatarFileChange = async (event_: React.ChangeEvent<HTMLInputElement>) => {
    const file = event_.target.files?.[0];
    if (!file) {
      return;
    }

    // Check if it exceeds 2MB
    if (file.size > 2 * 1024 * 1024) {
      try {
        // Compress to a maximum of 2MB (or less)
        const compressedFile = await compressImage(file, 2, 1200);
        await uploadAvatar(compressedFile);
      } catch (error) {
        console.error('Error compressing the image:', error);
      }
    } else {
      // If it's already <= 2MB, upload directly
      await uploadAvatar(file);
    }
  };

  /**
   * Function that sends a POST to your backend to save the avatar.
   */
  const uploadAvatar = async (imageFile: File) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!baseUrl) {
      showMessage('error', t(NOTIF_ERROR), t(API_BASE_URL_MISSING_KEY));
      return;
    }
    // Update state for preview
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      if (reader.result) {
        setUserData((previous) => ({ ...previous, avatar: reader.result as string }));
      }
    });
    reader.readAsDataURL(imageFile);

    // Upload to the server with formData
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('avatar', imageFile);

    try {
      await axios.post(`${baseUrl}/api/user/uploadAvatarToDB`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token ?? ''}`,
        },
      });
      showMessage('success', t(NOTIF_SUCCESS), t('profile.imageUpdated'));
    } catch (error) {
      console.error('Error uploading the avatar:', error);
    }
  };

  // Saves profile changes.
  const handleSave = async () => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
      if (!baseUrl) {
        showMessage('error', t(NOTIF_ERROR), t(API_BASE_URL_MISSING_KEY));
        return;
      }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${baseUrl}/api/user/updateUserSettings`, userData, {
        headers: { Authorization: `Bearer ${token ?? ''}` },
      });

      setIsEditing(false);
      showMessage('success', t(NOTIF_SUCCESS), t('profile.settingsUpdated'));
    } catch (error) {
      console.error('Error updating information:', error);
    }
  };

  if (isLoading) {
    return <div className="loading-spinner">{t('profile.loading')}</div>;
  }

  return (
    <div className="settings-form-container-profile">
      <h2 className="settings-form-title-profile">{t('profile.title')}</h2>

      <div className="avatar-section-profile">
        <div className="avatar-preview-profile">
          {userData.avatar ? (
            <img
              src={userData.avatar}
              alt="user avatar"
              className="avatar"
              onError={(_event) => {
                setUserData((previous) => ({ ...previous, avatar: '' }));
              }}
            />
          ) : (
            <DefaultAvatar className="default-avatar" />
          )}
          {isEditing && (
            <button className="change-avatar-btn" onClick={handleChangeAvatarClick}>
              {t('profile.changeImage')}
            </button>
          )}
          <input
            type="file"
            accept="image/*"
            ref={fileInputReference}
            style={{ display: 'none' }}
            onChange={handleAvatarFileChange}
          />
        </div>
      </div>

      <div className="form-group-profile" style={{ '--animation-order': 1 } as React.CSSProperties}>
        <label htmlFor="name">{t('profile.firstName')}</label>
        {isEditing ? (
          <input
            id="name"
            type="text"
            name="name"
            value={userData.name}
            onChange={handleInputChange}
          />
        ) : (
          <>
            <input
              id="name"
              type="text"
              value={userData.name}
              readOnly
              style={{ position: 'absolute', opacity: 0, height: 0, width: 0 }}
              aria-hidden="true"
              tabIndex={-1}
            />
            <div className="form-value-profile">{userData.name}</div>
          </>
        )}
      </div>

      <div className="form-group-profile" style={{ '--animation-order': 2 } as React.CSSProperties}>
        <label htmlFor="lastName">{t('profile.lastName')}</label>
        {isEditing ? (
          <input
            id="lastName"
            type="text"
            name="lastName"
            value={userData.lastName}
            onChange={handleInputChange}
          />
        ) : (
          <>
            <input
              id="lastName"
              type="text"
              value={userData.lastName}
              readOnly
              style={{ position: 'absolute', opacity: 0, height: 0, width: 0 }}
              aria-hidden="true"
              tabIndex={-1}
            />
            <div className="form-value-profile">{userData.lastName}</div>
          </>
        )}
      </div>

      <div className="form-group-profile" style={{ '--animation-order': 3 } as React.CSSProperties}>
        <label htmlFor="email">{t('profile.email')}</label>
        {isEditing ? (
          <input
            id="email"
            type="email"
            name="email"
            value={userData.email}
            onChange={handleInputChange}
          />
        ) : (
          <>
            <input
              id="email"
              type="text"
              value={userData.email}
              readOnly
              style={{ position: 'absolute', opacity: 0, height: 0, width: 0 }}
              aria-hidden="true"
              tabIndex={-1}
            />
            <div className="form-value-profile">{userData.email}</div>
          </>
        )}
      </div>

      <div className="form-group-profile" style={{ '--animation-order': 4 } as React.CSSProperties}>
        <label htmlFor="phoneNumber">{t('profile.phoneNumber')}</label>
        {isEditing ? (
          <input
            id="phoneNumber"
            type="tel"
            name="phoneNumber"
            value={userData.phoneNumber}
            onChange={handleInputChange}
          />
        ) : (
          <>
            <input
              id="phoneNumber"
              type="text"
              value={userData.phoneNumber}
              readOnly
              style={{ position: 'absolute', opacity: 0, height: 0, width: 0 }}
              aria-hidden="true"
              tabIndex={-1}
            />
            <div className="form-value-profile">{userData.phoneNumber}</div>
          </>
        )}
      </div>

      <div className="form-actions-profile">
        {isEditing ? (
          <>
            <button className="save-btn-profile" onClick={handleSave}>
              {t('profile.saveChanges')}
            </button>
            <button className="cancel-btn-profile" onClick={() => setIsEditing(false)}>
              {t('profile.cancel')}
            </button>
          </>
        ) : (
          <button className="edit-btn-profile" onClick={() => setIsEditing(true)}>
            {t('profile.editProfile')}
          </button>
        )}
      </div>
    </div>
  );
};
